import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AdminSidebar, StatCard, SkeletonCard, PageHeader } from "@/components/AdminLayout";
import { toast } from "sonner";
import api from "@/utils/api";

export default function ClientAdminDashboard() {
  const [stats, setStats] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, coursesRes, progressRes, logsRes] = await Promise.all([
        api.get("/analytics/dashboard"),
        api.get("/users"),
        api.get("/courses"),
        api.get("/progress/all").catch(() => ({ data: [] })),
        api.get("/audit-logs?limit=10").catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setWorkers(usersRes.data.filter(u => u.role === 'worker'));
      setCourses(coursesRes.data);
      setProgress(progressRes.data || []);
      setRecentActivity(logsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate derived stats
  const totalWorkers = workers.length;
  const activeWorkers = workers.filter(w => {
    const workerProgress = progress.filter(p => p.worker_id === w.id);
    return workerProgress.some(p => p.video_progress > 0 || p.completed);
  }).length;
  
  const completedTrainings = progress.filter(p => p.completed).length;
  const totalAssignments = progress.length;
  const overallCompletionRate = totalAssignments > 0 
    ? Math.round((completedTrainings / totalAssignments) * 100) 
    : 0;
  
  const avgQuizScore = progress.length > 0
    ? Math.round(progress.reduce((sum, p) => sum + (p.final_quiz_score || 0), 0) / progress.filter(p => p.final_quiz_score).length) || 0
    : 0;

  // Department stats
  const departments = [...new Set(workers.map(w => w.department).filter(Boolean))];
  const deptStats = departments.map(dept => {
    const deptWorkers = workers.filter(w => w.department === dept);
    const deptProgress = progress.filter(p => deptWorkers.find(w => w.id === p.worker_id));
    const completed = deptProgress.filter(p => p.completed).length;
    const total = deptProgress.length || 1;
    return {
      name: dept,
      completion: Math.round((completed / total) * 100),
      workers: deptWorkers.length
    };
  }).sort((a, b) => b.completion - a.completion);

  // Workers at risk (not started or low progress)
  const workersAtRisk = workers.filter(w => {
    const workerProgress = progress.filter(p => p.worker_id === w.id);
    if (workerProgress.length === 0) return true;
    const avgProgress = workerProgress.reduce((sum, p) => sum + (p.video_progress || 0), 0) / workerProgress.length;
    return avgProgress < 30 && !workerProgress.some(p => p.completed);
  });

  // Compliance status
  const greenWorkers = workers.filter(w => {
    const wp = progress.filter(p => p.worker_id === w.id);
    if (wp.length === 0) return false;
    return wp.every(p => p.completed);
  }).length;
  
  const redWorkers = workersAtRisk.length;
  const yellowWorkers = totalWorkers - greenWorkers - redWorkers;

  if (loading) {
    return (
      <div className="flex">
        <AdminSidebar active="dashboard" />
        <main className="admin-content">
          <div className="mb-6">
            <div className="h-8 bg-slate-200 rounded w-48 mb-2 animate-pulse"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex">
      <AdminSidebar active="dashboard" />
      <main className="admin-content">
        <PageHeader title="Dashboard" subtitle="VisualOps Training Portal">
          <Button
            onClick={() => navigate('/admin/workers')}
            variant="outline"
            className="border-[#2e7fd4] text-[#2e7fd4] hover:bg-[#2e7fd4] hover:text-white"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Add Worker
          </Button>
          <Button
            onClick={() => navigate('/admin/courses')}
            className="bg-[#0a2240] hover:bg-[#0f325c] text-white"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Course
          </Button>
        </PageHeader>

        {/* Top Stats Row - 4 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={<svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
            iconBg="bg-blue-100"
            value={totalWorkers}
            label="Total Workers"
          />
          <StatCard
            icon={<svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            iconBg="bg-green-100"
            value={completedTrainings}
            label="Completions"
          />
          <StatCard
            icon={<svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>}
            iconBg="bg-purple-100"
            value={`${avgQuizScore}%`}
            label="Avg Quiz Score"
          />
          <StatCard
            icon={<svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>}
            iconBg="bg-teal-100"
            value={`${overallCompletionRate}%`}
            label="Completion Rate"
          />
        </div>

        {/* Two Column Layout */}
        <div className="two-column-layout">
          {/* Left Column - Charts and Compliance */}
          <div className="space-y-6">
            {/* Compliance Status */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-4">Compliance Status</h3>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <p className="text-3xl font-bold text-green-600">{greenWorkers}</p>
                  <p className="text-sm text-green-700 mt-1">Compliant</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-xl">
                  <p className="text-3xl font-bold text-amber-600">{yellowWorkers}</p>
                  <p className="text-sm text-amber-700 mt-1">In Progress</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-xl">
                  <p className="text-3xl font-bold text-red-600">{redWorkers}</p>
                  <p className="text-sm text-red-700 mt-1">At Risk</p>
                </div>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
                <div className="bg-green-500 h-full" style={{ width: `${(greenWorkers / totalWorkers) * 100}%` }}></div>
                <div className="bg-amber-500 h-full" style={{ width: `${(yellowWorkers / totalWorkers) * 100}%` }}></div>
                <div className="bg-red-500 h-full" style={{ width: `${(redWorkers / totalWorkers) * 100}%` }}></div>
              </div>
            </div>

            {/* Department Progress */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-4">Department Progress</h3>
              <div className="space-y-4">
                {deptStats.length > 0 ? deptStats.map((dept, idx) => (
                  <div key={idx}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">{dept.name}</span>
                      <span className="text-sm font-bold text-slate-900">{dept.completion}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          dept.completion >= 80 ? 'bg-green-500' : 
                          dept.completion >= 50 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${dept.completion}%` }}
                      ></div>
                    </div>
                  </div>
                )) : (
                  <p className="text-sm text-slate-500 text-center py-4">No department data available</p>
                )}
              </div>
            </div>

            {/* Courses Overview */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Courses</h3>
                <button 
                  onClick={() => navigate('/admin/courses')}
                  className="text-sm text-[#2e7fd4] hover:underline font-medium"
                >
                  View All →
                </button>
              </div>
              <div className="space-y-3">
                {courses.slice(0, 4).map((course) => {
                  const courseProgress = progress.filter(p => p.course_id === course.id);
                  const enrolled = courseProgress.length;
                  const completed = courseProgress.filter(p => p.completed).length;
                  const rate = enrolled > 0 ? Math.round((completed / enrolled) * 100) : 0;
                  
                  return (
                    <div key={course.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="w-10 h-10 bg-[#0a2240] rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{course.title}</p>
                        <p className="text-xs text-slate-500">{enrolled} enrolled • {completed} completed</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${rate >= 70 ? 'text-green-600' : rate >= 40 ? 'text-amber-600' : 'text-slate-400'}`}>
                          {rate}%
                        </p>
                      </div>
                    </div>
                  );
                })}
                {courses.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No courses created yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Activity Feed and Pending Actions */}
          <div className="space-y-6">
            {/* Activity Feed */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-4">Recent Activity</h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {recentActivity.length > 0 ? recentActivity.map((log, idx) => (
                  <div key={idx} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0">
                    <div className="w-2 h-2 bg-[#2e7fd4] rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm text-slate-900">{log.action || 'Activity'}</p>
                      <p className="text-xs text-slate-500">
                        {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'Just now'}
                      </p>
                    </div>
                  </div>
                )) : (
                  <p className="text-sm text-slate-500 text-center py-4">No recent activity</p>
                )}
              </div>
            </div>

            {/* Workers at Risk */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Workers at Risk</h3>
                <span className="badge badge-danger">{workersAtRisk.length}</span>
              </div>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {workersAtRisk.slice(0, 5).map((worker) => (
                  <div key={worker.id} className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{worker.full_name}</p>
                      <p className="text-xs text-slate-500">{worker.department || 'No department'}</p>
                    </div>
                  </div>
                ))}
                {workersAtRisk.length === 0 && (
                  <div className="text-center py-4">
                    <svg className="w-10 h-10 mx-auto text-green-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-green-700 font-medium">All workers on track!</p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-3">
                <button 
                  onClick={() => navigate('/admin/workers')}
                  className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-left"
                >
                  <svg className="w-6 h-6 text-blue-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  <p className="text-sm font-medium text-slate-900">Add Worker</p>
                </button>
                <button 
                  onClick={() => navigate('/admin/courses')}
                  className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-left"
                >
                  <svg className="w-6 h-6 text-purple-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <p className="text-sm font-medium text-slate-900">Create Course</p>
                </button>
                <button 
                  onClick={() => navigate('/admin/analytics')}
                  className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-left"
                >
                  <svg className="w-6 h-6 text-teal-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p className="text-sm font-medium text-slate-900">View Reports</p>
                </button>
                <button 
                  onClick={() => {
                    toast.info("Export feature coming soon!");
                  }}
                  className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-left"
                >
                  <svg className="w-6 h-6 text-amber-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <p className="text-sm font-medium text-slate-900">Export Data</p>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
